import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useSegmentAnalyticsContext } from '@/SegmentAnalyticsProvider';
import { appOrganizationPath } from '@/utils/constants';

import { OnboardingCardProps } from '../components/OnboardingCard';
import { OnboardingStepV2 } from '../constants';

export const useOnboardingEntrypointOptions = ({
  newTenantId,
}: {
  newTenantId: string | undefined;
}) => {
  const [_, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { analytics } = useSegmentAnalyticsContext();

  return useMemo<OnboardingCardProps[]>(() => {
    return [
      {
        title: 'Trace an Application',
        description: 'Analyze and debug applications using traces.',
        onClick: () => {
          analytics?.track('Onboarding Card Clicked', {
            card_name: 'Trace an Application',
          });
          if (newTenantId) {
            setSearchParams({
              organizationId: newTenantId,
              step: OnboardingStepV2.SetupObservability.toString(),
            });
          }
        },
        helperText: 'Start tracing',
        dataTestId: 'onboarding-trace-application',
      },
      {
        title: 'Test Prompts',
        description:
          'Iterate and test on prompts across any model or provider.',
        onClick: () => {
          analytics?.track('Onboarding Card Clicked', {
            card_name: 'Test Prompts in the Playground',
          });
          if (newTenantId) {
            navigate(`/${appOrganizationPath}/${newTenantId}/playground`);
          }
        },
        helperText: 'Start prompting',
        dataTestId: 'onboarding-test-prompts',
      },
      {
        title: 'Run an Evaluation',
        description:
          'Measure app performance: identify failures, compare changes, ensure reliability.',
        onClick: () => {
          analytics?.track('Onboarding Card Clicked', {
            card_name: 'Run an Evaluation',
          });
          if (newTenantId) {
            setSearchParams({
              organizationId: newTenantId,
              step: OnboardingStepV2.SetupEvaluation.toString(),
            });
          }
        },
        helperText: 'Start evaluating',
        dataTestId: 'onboarding-run-evaluation',
      },
      {
        title: 'Deploy an Agent',
        description:
          'Deploy agentic applications to production with LangGraph Platform.',
        onClick: () => {
          analytics?.track('Onboarding Card Clicked', {
            card_name: 'Deploy Agents',
          });
          if (newTenantId) {
            navigate(`/${appOrganizationPath}/${newTenantId}/host/deployments`);
          }
        },
        helperText: 'Start deploying',
        dataTestId: 'onboarding-deploy-agents',
      },
    ];
  }, [newTenantId, setSearchParams, navigate]);
};
